import { PROGRAM_STATUSES } from 'src/constants/programs';

function prepareProgramTreeMap(
  treatmentAreas,
  programs,
) {
  const treatmentAreaById = new Map();
  const subTreatmentAreaById = new Map();
  const subTreatmentAreaParentId = new Map();

  treatmentAreas.forEach((ta) => {
    const treatmentArea = {
      id: ta.id,
      label: ta.label,
      order: ta.order,
    };

    if (ta.parent === null) {
      treatmentAreaById.set(ta.id, treatmentArea);
    } else {
      subTreatmentAreaById.set(ta.id, treatmentArea);
      subTreatmentAreaParentId.set(ta.id, ta.parent);
    }
  });

  const programTreeMap = new Map();
  programs.forEach((program) => {
    let treatmentAreaId = program.treatmentArea || null;
    let subTreatmentAreaId = program.subTreatmentArea || null;

    // Check if treatment area is valid - or reset it to null
    if (treatmentAreaId !== null && !treatmentAreaById.has(treatmentAreaId)) {
      treatmentAreaId = null;
      subTreatmentAreaId = null;
    }
    // Check if sub-treatment area is valid and correctly associated - or reset it to null
    if (subTreatmentAreaId !== null
      && (
        !subTreatmentAreaById.has(subTreatmentAreaId)
        || subTreatmentAreaParentId.get(subTreatmentAreaId) !== treatmentAreaId)
    ) {
      // Only reset sub-treatment area - we maintain the main treatment area if valid
      subTreatmentAreaId = null;
    }

    // Ensure treatment area exists in tree
    if (programTreeMap.has(treatmentAreaId) === false) {
      if (treatmentAreaId === null) {
        programTreeMap.set(null, {
          id: null,
          label: null,
          order: -1,
          subTreatmentAreas: new Map(),
        });
      } else {
        programTreeMap.set(treatmentAreaId, {
          ...treatmentAreaById.get(treatmentAreaId),
          subTreatmentAreas: new Map(),
        });
      }
    }

    // Ensure sub-treatment area exists in tree
    const treatmentAreaNode = programTreeMap.get(treatmentAreaId);
    if (treatmentAreaNode.subTreatmentAreas.has(subTreatmentAreaId) === false) {
      if (subTreatmentAreaId === null) {
        treatmentAreaNode.subTreatmentAreas.set(null, {
          id: null,
          label: null,
          order: -1,
          programs: [],
        });
      } else {
        treatmentAreaNode.subTreatmentAreas.set(subTreatmentAreaId, {
          ...subTreatmentAreaById.get(subTreatmentAreaId),
          programs: [],
        });
      }
    }

    // Add program to sub-treatment area
    const subTreatmentAreaNode = treatmentAreaNode.subTreatmentAreas.get(subTreatmentAreaId);
    subTreatmentAreaNode.programs.push(program);
  });
  return programTreeMap;
} // prepareProgramTreeMap()

/**
 * Builds a hierarchical program tree structure from the given treatment areas and programs.
 *
 * Maintains the original sort-order of programs within each sub-treatment area, which
 * is important to ensure that callers can trust that indexes within the tree remain consistent.
 *
 * @param {Array} treatmentAreas Array of treatment area objects (must have id, label, and order properties).
 * @param {Array} programs Array of program objects.
 * @returns {Array} Hierarchical program tree structure.
 */
export function buildProgramTree(
  treatmentAreas,
  programs,
) {
  const programTreeMap = prepareProgramTreeMap(
    treatmentAreas,
    programs,
  );

  return Array.from(programTreeMap.values())
    .map((taNode) => ({
      treatmentAreaId: taNode.id,
      treatmentAreaLabel: taNode.label,
      order: taNode.order,
      subTreatmentAreas: Array.from(taNode.subTreatmentAreas.values())
        .map((subTaNode) => ({
          treatmentAreaId: subTaNode.id,
          subTreatmentAreaLabel: subTaNode.label,
          order: subTaNode.order,
          programs: subTaNode.programs,
        }))
        .sort((a, b) => a.order - b.order),
    }))
    .sort((a, b) => a.order - b.order);
} // buildProgramTree()

/**
 * Builds a flat program tree structure from the given programs.
 * @param {Array} programs Array of program objects.
 * @returns {Array} Flat program tree structure.
 */
export function buildFlatProgramTree(programs) {
  return [
    {
      treatmentAreaId: null,
      treatmentAreaLabel: null,
      subTreatmentAreas: [
        {
          subTreatmentAreaId: null,
          subTreatmentAreaLabel: null,
          programs,
        },
      ],
    },
  ];
} // buildFlatProgramTree()

/**
 * Prepares a Set of treatment area IDs from the given treatment areas info array.
 * This is a requried precondition for getProgramIndexInTreatmentArea() to prevent us
 * from having to build this set repeatedly on multiple calls to getProgramIndexInTreatmentArea().
 * @param {Array} treatmentAreas List of treatment area objects.
 * @returns {Set} Set of treatment area IDs.
 */
export function prepareTreatmentAreaIdSet(treatmentAreas) {
  return new Set(treatmentAreas.map((ta) => ta.id));
} // prepareTreatmentAreaIdSet()

/**
 * Get the index of a program within its treatment area and sub-treatment area group.
 * @param {Set} treatmentAreaIdSet
 * @param {Array} programs
 * @param {Object} program
 * @returns
 */
export function getProgramIndexInTreatmentArea(treatmentAreaIdSet, programs, program) {
  const treatmentAreaId = treatmentAreaIdSet.has(program.treatmentArea) ? program.treatmentArea : null;
  const subTreatmentAreaId = treatmentAreaIdSet.has(program.subTreatmentArea) ? program.subTreatmentArea : null;

  const programsInTA = programs
    .filter(
      (p) => ((p.treatmentArea || null) === treatmentAreaId && (p.subTreatmentArea || null) === subTreatmentAreaId),
    );
  return programsInTA.findIndex((p) => p.id === program.id);
} // getProgramIndexInTreatmentArea()

/**
 * Builds the program object for a new program created from a template (client or library).
 * A template's own status history must not carry over.
 */
export function buildProgramFromTemplate(source, context) {
  const {
    route,
    autoAssignClient,
    currentClient,
    autoAssignClinic,
    userDetails,
    useFolder,
    currentFolder,
    packageId = null,
    parentFolderId = null,
  } = context;

  return {
    ...source,
    id: null,
    // Behaviour programs can't change status in the UI, so default them to active.
    status: source.type === 'behaviour' ? 'active' : 'waiting',
    statusChanges: {},
    programName: 'clear' in (route?.query ?? {}) ? '' : source.programName,
    client: autoAssignClient ? currentClient.id : null,
    clinic: autoAssignClinic ? (userDetails?.clinic ?? null) : null,
    package: packageId,
    createdByUser: userDetails.userId,
    createdByUserName: userDetails.name,
    updatedByUserName: userDetails.name,
    parentFolder: useFolder && (parentFolderId || currentFolder)
      ? parentFolderId || (currentFolder?.id ?? null)
      : null,
  };
} // buildProgramFromTemplate()

/**
 * The program's status changes other than `active`, oldest first — `active` is left out because it
 * is shown on its own as the active start date, and a status the program never entered has no
 * recorded timestamp to show.
 *
 * @param {object} program
 * @returns {Array<{ status: string, timestamp: object }>}
 */
export function getStatusChangeHistory(program) {
  return Object.entries(program?.statusChanges ?? {})
    .filter(([status, timestamp]) => status !== PROGRAM_STATUSES.ACTIVE && !!timestamp)
    .map(([status, timestamp]) => ({ status, timestamp }))
    .sort((a, b) => (a.timestamp.seconds ?? 0) - (b.timestamp.seconds ?? 0));
}

/**
 * Returns a sum by the filtered statuses.
 * @param {Array<String>} statuses
 * @param {Record<string, number>} counts
 */
function sumOfStatus(statuses, counts) {
  return statuses.reduce((sum, status) => sum + (counts[status] ?? 0), 0);
} // sumOfStatus()

/**
 * Share of a program's targets that are mastered or closed, rounded to a whole percentage.
 * @param {Array<{status: String, count: Number}>} targetsPerStatus
 * @returns {Number|null} null when the program has no targets to measure against
 */
export function getProgramCompletionPercentage(targetsPerStatus) {
  const counts = Object.fromEntries(
    (targetsPerStatus || []).map(({ status, count }) => [status, count ?? 0]),
  );

  const completed = sumOfStatus(['mastered', 'closed'], counts);
  const total = sumOfStatus(['waiting', 'probe', 'acquisition', 'mastered', 'closed'], counts);
  return total > 0 ? Math.round((completed * 100) / total) : null;
} // getProgramCompletionPercentage()
